import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Users, 
  MessageSquare, 
  TrendingUp, 
  Clock, 
  Target, 
  CheckCircle2,
  Star,
  ArrowRight,
  Linkedin,
  BarChart3,
  Zap,
  XCircle,
  RefreshCw,
  Settings,
  LineChart,
  Shield,
  Handshake
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactForm from '../components/ContactForm';
import TestimonialSection from '../components/TestimonialSection';
import ComparisonTable from '../components/ComparisonTable';
import ProcessSteps from '../components/ProcessSteps';
import { MOCK_DATA } from '../utils/mock';

export const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    contactSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header onBookCall={scrollToContact} />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-white -z-10" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{ animationDelay: '2s' }} />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200 text-sm px-4 py-2 font-medium">
                Über 500 strategisch betreute LinkedIn-Kampagnen
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
                LinkedIn-Vertrieb auf{' '}
                <span className="text-blue-600">CEO-Niveau</span> –{' '}
                <span className="text-cyan-600">Wie ein Klon von dir!</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed">
                Als Geschäftsführer solltest du keine Nachrichten schreiben. Du solltest Gespräche führen. 
                <strong> Wir bauen und betreiben dein persönliches LinkedIn-Vertriebssystem.</strong> Ein AI-gestützter 
                Agent kommuniziert in deinem Stil – gesteuert, geprüft und kontinuierlich optimiert durch unser 
                Experten-Team. Dein Aufwand: <strong>nur 30 Minuten pro Monat.</strong>
              </p>
              
              <div className="flex flex-wrap gap-6 items-center">
                <a 
                  href="https://calendly.com/patrick-2besales/linkedin-ai-agent-beratungsgesprach"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    Strategische Potenzialanalyse
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 border-2 border-white" />
                    ))}
                  </div>
                  <div className="ml-2">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                    <p className="text-sm text-slate-600 font-medium">4,8/5 auf Google</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-3xl transform rotate-3 opacity-20" />
              <img 
                src="https://images.pexels.com/photos/8728285/pexels-photo-8728285.jpeg" 
                alt="AI Agent Technology" 
                className="relative rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section - Für wen / Nicht für wen */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900">
              Für Geschäftsführer, die LinkedIn strategisch nutzen wollen.
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Wir arbeiten NICHT mit */}
            <Card className="border-2 border-red-200 bg-red-50/50">
              <CardContent className="p-8 space-y-6">
                <h3 className="text-2xl font-bold text-slate-900">Wir arbeiten nicht mit:</h3>
                <ul className="space-y-4">
                  {[
                    'DIY-Testern',
                    '„Mal schauen"-Projekten',
                    'Tool-Suchern'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                      <span className="text-slate-700 text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
            
            {/* Wir arbeiten MIT */}
            <Card className="border-2 border-green-200 bg-green-50/50">
              <CardContent className="p-8 space-y-6">
                <h3 className="text-2xl font-bold text-slate-900">Wir arbeiten mit Unternehmern, die:</h3>
                <ul className="space-y-4">
                  {[
                    'Vertrieb delegieren',
                    'Struktur statt Zufall wollen',
                    'LinkedIn als strategischen Kanal etablieren möchten'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                      <span className="text-slate-700 text-lg">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <Badge className="bg-cyan-100 text-cyan-700 text-sm px-4 py-2">Deine Vorteile</Badge>
            <h2 className="text-4xl font-bold text-slate-900">
              Dein Zeitaufwand mit uns: Nur 30 Minuten im Monat
            </h2>
            <p className="text-xl text-slate-600">Dein Ergebnis: Kontinuierlich neue Geschäftskontakte</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {MOCK_DATA.benefits.map((benefit, idx) => (
              <Card key={idx} className="border-2 hover:border-blue-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1">
                <CardContent className="p-8 space-y-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center">
                    {benefit.icon}
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">{benefit.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-cyan-600 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-8">
          <h2 className="text-4xl font-bold">
            Über 500 strategisch betreute LinkedIn-Kampagnen
          </h2>
          <div className="grid md:grid-cols-4 gap-8 mt-12">
            {MOCK_DATA.stats.map((stat, idx) => (
              <div key={idx} className="space-y-2">
                <div className="text-5xl font-bold">{stat.value}</div>
                <div className="text-blue-100 text-lg">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialSection />

      {/* Why Customers Stay Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 space-y-4">
            <Badge className="bg-blue-100 text-blue-700 text-sm px-4 py-2">Langfristige Partnerschaften</Badge>
            <h2 className="text-4xl font-bold text-slate-900">
              Warum unsere Kunden langfristig bleiben
            </h2>
            <p className="text-xl text-slate-600">
              Nicht wegen eines AI-Agenten. Sondern wegen:
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: <RefreshCw className="w-6 h-6 text-white" />, title: 'Kontinuierlicher Funnel-Optimierung' },
              { icon: <Users className="w-6 h-6 text-white" />, title: 'Laufender Leadlisten-Erneuerung' },
              { icon: <MessageSquare className="w-6 h-6 text-white" />, title: 'Messaging-Feintuning' },
              { icon: <TrendingUp className="w-6 h-6 text-white" />, title: 'Marktanpassung' },
              { icon: <LineChart className="w-6 h-6 text-white" />, title: 'KPI-Transparenz' },
              { icon: <Handshake className="w-6 h-6 text-white" />, title: 'Strategischer Betreuung' }
            ].map((item, idx) => (
              <Card key={idx} className="border-2 hover:border-blue-300 transition-all duration-300">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <span className="text-lg font-medium text-slate-800">{item.title}</span>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="mt-12 bg-slate-100 rounded-2xl p-8 text-center">
            <p className="text-xl text-slate-700 leading-relaxed">
              LinkedIn verändert sich. <strong>Dein System wird permanent angepasst.</strong>
            </p>
            <p className="text-2xl font-bold text-blue-600 mt-4">
              Das ist kein Setup. Das ist Betrieb.
            </p>
          </div>
        </div>
      </section>

      {/* Value of Conversation Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <h2 className="text-4xl font-bold text-white">
            Was dich ein einzelnes qualifiziertes Entscheider-Gespräch wert ist
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed">
            Wenn ein Gespräch nur einen neuen Auftrag generiert, rechnet sich das System mehrfach.
          </p>
          <p className="text-2xl font-semibold text-cyan-400">
            Wir optimieren nicht auf Likes. Wir optimieren auf relevante Gespräche.
          </p>
        </div>
      </section>

      {/* Comparison Table */}
      <ComparisonTable />

      {/* Process Steps */}
      <ProcessSteps />

      {/* Not For You Section */}
      <section className="py-20 px-6 bg-slate-100">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl p-8 lg:p-12 border-2 border-slate-200">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Dieses Angebot ist nicht geeignet für dich, wenn …
            </h2>
            <ul className="space-y-4 mb-8">
              {[
                'du nur ein AI-Tool suchst',
                'du LinkedIn selbst testen möchtest',
                'du kurzfristige Experimente planst'
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3">
                  <XCircle className="w-6 h-6 text-red-500 flex-shrink-0" />
                  <span className="text-slate-700 text-lg">{item}</span>
                </li>
              ))}
            </ul>
            <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
              <p className="text-lg text-slate-800 font-medium">
                Wir übernehmen Verantwortung für deinen LinkedIn-Vertrieb. <strong>Dafür braucht es Commitment.</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why LinkedIn Section */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf" 
                alt="Team Collaboration" 
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="space-y-6">
              <Badge className="bg-blue-100 text-blue-700">Unsere Erfolgsformel</Badge>
              <h2 className="text-4xl font-bold text-slate-900">
                LinkedIn + Experten-Team + AI-System
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Mit mehr als <strong>24 Millionen Nutzern</strong> allein in der DACH-Region ist LinkedIn 
                die perfekte Plattform für B2B-Akquise. Wir kombinieren die Reichweite 
                von LinkedIn mit der Erfahrung unseres <strong>Experten-Teams</strong> und der Effizienz eines 
                <strong> AI-gestützten Vertriebssystems</strong>.
              </p>
              <div className="space-y-4 pt-4">
                {[
                  '✓ LinkedIn: 24 Millionen B2B-Entscheider in DACH',
                  '✓ Experten-Team: Kontinuierliche Optimierung',
                  '✓ AI-System: Skalierbare, personalisierte Kommunikation',
                  '✓ Ergebnis: Planbare Entscheider-Gespräche'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-12 lg:p-16 text-white">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h2 className="text-4xl font-bold">
                  Über uns
                </h2>
                <p className="text-xl text-slate-300 leading-relaxed">
                  <strong className="text-white">Patrick Häfner</strong> ist seit 2010 Experte für 
                  Sales und digitale Neukunden-Akquise. Als Pionier im Bereich AI-gestützter LinkedIn-Kommunikation 
                  entwickelte er das LinkedIn-Vertriebssystem für CEOs.
                </p>
                <p className="text-lg text-slate-400 leading-relaxed">
                  In seiner Laufbahn hat er bereits mehr als <strong className="text-white">500 strategisch 
                  betreute LinkedIn-Kampagnen</strong> für namhafte Kunden wie <strong>Siemens, Cornelsen & TÜV</strong> betreut. 
                  Heute kombiniert er jahrelange Erfahrung mit modernster AI-Technologie.
                </p>
              </div>
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl transform rotate-3 opacity-20" />
                <img 
                  src="/patrick-haefner.jpg" 
                  alt="Patrick Häfner" 
                  className="relative rounded-2xl shadow-2xl w-full max-w-xs mx-auto h-auto object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section id="contact" className="py-24 px-6 bg-gradient-to-br from-blue-600 to-cyan-600">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            Bereit für dein LinkedIn-Vertriebssystem auf CEO-Niveau?
          </h2>
          <p className="text-xl text-blue-100 leading-relaxed">
            In der strategischen Potenzialanalyse zeigen wir dir, wie wir dein LinkedIn-Vertriebssystem aufbauen 
            und <strong className="text-white">planbare Entscheider-Gespräche</strong> generieren. 
            Du erhältst eine <strong className="text-white">individuelle Strategie</strong>, 
            die auf deine Branche und Zielgruppe zugeschnitten ist.
          </p>
          
          <div className="bg-white rounded-3xl p-8 lg:p-12 shadow-2xl">
            <ContactForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
